import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import ProductSlider from '../components/ProductSlider';
// RecentlyViewed removed per client request
import HeroSlider from '../components/HeroSlider';
// ShopByUseCase removed per client request
// ProductBundles removed per client request
import PressSection from '../components/PressSection';
import CustomerTestimonials from '../components/CustomerTestimonials';
import { ShieldCheck, Truck, UserCheck, Building2, Users, Award, TrendingUp, CheckCircle2, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { FullPageLoader } from '../components/MedicalLoader';
import { useCart } from '../contexts/CartContext';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const productsRes = await api.get('/products');
      setFeaturedProducts(productsRes.data); // Get all products for slider
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (product) => {
    await addToCart(product, 1);
  };

  if (loading) {
    return <FullPageLoader text="Loading Alaxico..." />;
  }

  return (
    <div className="animate-fade-in" data-testid="home-page">
      {/* Hero Slider */}
      <HeroSlider />

      {/* Trust Badges Section */}
      <section className="py-8 sm:py-12 bg-purple-50 border-b border-purple-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {[
              { id: 'iso', icon: ShieldCheck, title: 'ISO Certified', desc: 'Quality Assured', color: 'text-purple-600' },
              { id: 'delivery', icon: Truck, title: 'Fast Delivery', desc: 'Pan India', color: 'text-purple-600' },
              { id: 'warranty', icon: Award, title: 'Warranty', desc: 'On All Products', color: 'text-purple-600' },
              { id: 'support', icon: UserCheck, title: '24/7 Support', desc: 'Expert Help', color: 'text-purple-600' },
            ].map((feature) => (
              <div 
                key={feature.id}
                className="flex items-center space-x-3 p-3 sm:p-4 rounded-xl bg-white border border-purple-100 hover:border-purple-200 hover:shadow-sm transition-all"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <feature.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${feature.color}`} />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-semibold text-gray-900">{feature.title}</h3>
                  <p className="text-xs sm:text-sm text-gray-500">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products - Horizontal Slider */}
      <ProductSlider
        products={featuredProducts}
        title="Featured Products"
        subtitle="Top-quality medical equipment for professionals"
        onAddToCart={handleAddToCart}
        viewAllLink="/products"
      />

      {/* Customer Testimonials - After Featured Products */}
      <CustomerTestimonials />

      {/* Press & Media Section */}
      <PressSection />

      {/* Why Choose Us */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900 mb-2 sm:mb-4">
              Why Healthcare Professionals <span className="text-purple-600">Choose Us</span>
            </h2>
            <p className="text-sm sm:text-base lg:text-xl text-gray-500">Join 1000+ satisfied healthcare facilities</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {[
              { id: 'quality', icon: Award, title: 'Quality Assured', desc: 'All products are quality checked and certified' },
              { id: 'prices', icon: TrendingUp, title: 'Best Prices', desc: 'Competitive pricing with bulk order discounts' },
              { id: 'experts', icon: Users, title: 'Expert Support', desc: 'Dedicated support team for all your needs' },
            ].map((item) => (
              <div key={item.id} className="text-center p-4 sm:p-6 lg:p-8 bg-purple-50 rounded-xl sm:rounded-2xl border border-purple-100 hover:border-purple-200 transition-all duration-300 hover:shadow-lg">
                <div className="w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 mx-auto mb-3 sm:mb-4 lg:mb-6 bg-purple-100 rounded-xl sm:rounded-2xl flex items-center justify-center">
                  <item.icon className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10 text-purple-600" />
                </div>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold mb-1 sm:mb-2 lg:mb-3 text-gray-900">{item.title}</h3>
                <p className="text-xs sm:text-sm lg:text-base text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-purple-600" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-3 sm:mb-4 lg:mb-6" data-testid="cta-title">
            Ready to Get Started?
          </h2>
          <p className="text-sm sm:text-base lg:text-xl text-purple-100 mb-6 sm:mb-8">
            Get special pricing for bulk orders. Our team will contact you within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
            <Link to="/bulk-order" data-testid="cta-bulk-enquiry-button">
              <Button size="lg" className="w-full sm:w-auto bg-white text-purple-600 hover:bg-purple-50 shadow-xl transform hover:scale-105 transition-all duration-300 text-sm sm:text-base lg:text-lg px-6 sm:px-8 py-4 sm:py-6">
                Request Bulk Quote
                <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
            </Link>
            <Link to="/products">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white/10 shadow-xl transform hover:scale-105 transition-all duration-300 text-sm sm:text-base lg:text-lg px-6 sm:px-8 py-4 sm:py-6">
                Browse Products
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
